import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import useUpdateShopPreferences from "@babackapp/private-api-client/client/use-update-shop-preferences";
import useTranslation from "@babackapp/translations/use-translation";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@babackapp/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@babackapp/ui/components/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@babackapp/ui/components/dialog";
import { Form, FormField } from "@babackapp/ui/components/form";
import { Input } from "@babackapp/ui/components/input";
import { Switch } from "@babackapp/ui/components/switch";
import { zodResolver } from "@hookform/resolvers/zod";
import { Settings } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useAnalyticsFilters } from "../hooks/use-analytics-filters";

const analyticsSettingsSchema = z.object({
  minOrdersToDisplay: z.coerce.number().min(1).max(10000),
  hideOrphanProducts: z.boolean(),
});

type AnalyticsSettingsFormValues = z.infer<typeof analyticsSettingsSchema>;

export function AnalyticsSettingsDialog() {
  const t = useTranslation();
  const [open, setOpen] = useState(false);
  const { data: shop } = useCurrentShopSuspense();
  const queryClient = useQueryClient();

  const currentMinOrders =
    shop.preferences?.stats?.returnRates?.minOrdersToDisplay ?? 1;
  const currentHideOrphanProducts =
    shop.preferences?.stats?.returnRates?.hideOrphanProducts ?? false;

  const form = useForm<AnalyticsSettingsFormValues>({
    resolver: zodResolver(analyticsSettingsSchema),
    defaultValues: {
      minOrdersToDisplay: currentMinOrders,
      hideOrphanProducts: currentHideOrphanProducts,
    },
    values: {
      minOrdersToDisplay: currentMinOrders,
      hideOrphanProducts: currentHideOrphanProducts,
    },
  });

  const { includeAllOrders, setIncludeAllOrders } = useAnalyticsFilters();

  const { mutateAsync: updatePreferences, isPending } =
    useUpdateShopPreferences(shop.id);

  const onSubmit = form.handleSubmit(async (data) => {
    await updatePreferences({
      stats: {
        returnRates: {
          minOrdersToDisplay: data.minOrdersToDisplay,
          hideOrphanProducts: data.hideOrphanProducts,
        },
      },
    });
    // If setting minOrders back to 1, "include all orders" filter is meaningless
    if (data.minOrdersToDisplay <= 1 && includeAllOrders) {
      setIncludeAllOrders(false);
    }
    setOpen(false);
    // Invalidate in background after closing to avoid re-render keeping dialog open
    queryClient.invalidateQueries({
      queryKey: ["get", "/shops/{shopId}/statistics/products-return-rates"],
    });
    queryClient.invalidateQueries({
      queryKey: ["get", "/shops/{shopId}/statistics/return-requests-rates"],
    });
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" className="h-9 w-9">
          <Settings className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("analytics.settings.title")}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={onSubmit}>
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">
                  {t("analytics.settings.minOrders.title")}
                </CardTitle>
                <CardDescription>
                  {t("analytics.settings.minOrders.description")}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <FormField
                  control={form.control}
                  name="minOrdersToDisplay"
                  render={({ field }) => (
                    <Input
                      type="number"
                      min={1}
                      max={10000}
                      className="w-32"
                      {...field}
                    />
                  )}
                />
              </CardContent>
            </Card>
            <Card className="mt-4">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-sm">
                      {t("analytics.settings.hideOrphanProducts.title")}
                    </CardTitle>
                    <CardDescription>
                      {t("analytics.settings.hideOrphanProducts.description")}
                    </CardDescription>
                  </div>
                  <FormField
                    control={form.control}
                    name="hideOrphanProducts"
                    render={({ field }) => (
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    )}
                  />
                </div>
              </CardHeader>
            </Card>
            <div className="flex justify-end mt-4">
              <Button type="submit" disabled={isPending}>
                {t("misc.save")}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
